<template>
  <section
    class="relative mt-6 md:mt-7 rounded-xl border border-boba-grey/50 bg-boba-sand-light shadow-sm px-5 pt-8 pb-5 md:px-8 md:pt-8 md:pb-6"
  >
    <scoreboard-countdown
      class="absolute right-5 -top-5 md:right-8 md:-top-6"
      :to="countdownTo"
      @ended="callIfNotEnded"
      :key="countdownTo.toMillis()"
    >
      <template
        v-if="
          !listing.current_price &&
          DateTime.fromISO(listing.starts_at) > DateTime.now()
        "
        #label
      >
        <p
          class="flex items-center gap-x-1.5 md:justify-start md:pb-1 text-sm leading-none"
        >
          <i class="fa-solid fa-clock text-sm md:text-base" aria-hidden="true" />
          <span>Begins in</span>
        </p>
      </template>
    </scoreboard-countdown>

    <h2
      class="text-xl md:text-2xl font-semibold tracking-tight text-boba-brown md:pr-40"
    >
      <span v-if="listing.current_price">Current Price</span>
      <span
        v-else-if="DateTime.fromISO(listing.starts_at) > DateTime.now()"
        class="text-boba-grey-dark"
      >
        Not available yet
      </span>
      <span v-else>Auction Ended</span>
    </h2>

    <p
      v-if="listing.current_price"
      class="mt-2 text-4xl md:text-6xl font-semibold tracking-tighter text-boba-brown"
    >
      <span>{{ $format(listing.current_price.price / 1e9) }}</span>
      <span class="ml-1 text-xl md:text-3xl font-normal tracking-tight text-boba-grey-dark">
        miles/ea.
      </span>
    </p>
    <login v-else-if="DateTime.fromISO(listing.starts_at) > DateTime.now()">
      <template #fallback> </template>
    </login>

    <login v-if="listing.current_price">
      <template #fallback="{ loginURI, register }">
        <div class="mt-4 border-t border-boba-grey/50"></div>
        <button
          type="button"
          class="mt-4 h-12 w-full rounded-lg bg-primary-btn-bg text-primary-btn-txt text-base font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-btn-bg focus-visible:ring-offset-2 focus-visible:ring-offset-boba-sand-light"
          @click="register"
        >
          Register Now
        </button>
        <p class="mt-3 text-sm text-boba-grey-dark">
          Already have an account?
          <a
            :href="loginURI"
            class="text-boba-brown font-semibold underline underline-offset-2"
            >Log in</a
          >
        </p>
      </template>
      <template #default>
        <div class="mt-4 border-t border-boba-grey/50"></div>
        <div
          class="mt-4 grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-3 gap-y-3 items-end md:grid-cols-[7.5rem_minmax(11rem,0.9fr)] md:gap-x-4"
        >
          <div>
            <div class="flex items-baseline justify-between gap-x-2 mb-1.5">
              <label
                for="live-bid-quantity"
                class="text-xs font-semibold uppercase tracking-wide text-boba-grey-dark"
              >
                Quantity
              </label>
              <span
                v-if="hasPublicLimit"
                class="text-xs text-boba-grey-dark tabular-nums"
              >
                Limit {{ quantityLimit }}
              </span>
            </div>
            <select
              v-if="hasPublicLimit"
              id="live-bid-quantity"
              v-model="bidQuantity"
              class="h-12 w-full rounded-lg border border-boba-grey bg-white px-3 pr-8 text-lg tabular-nums focus:border-boba-brown focus:ring-2 focus:ring-boba-brown/20 outline-none"
            >
              <option value="0"></option>
              <option v-for="qty in quantityLimit" :key="qty" :value="qty">
                {{ qty }}
              </option>
            </select>
            <div
              v-else
              class="flex h-12 items-center rounded-lg border border-boba-grey bg-white overflow-hidden focus-within:border-boba-brown focus-within:ring-2 focus-within:ring-boba-brown/20"
            >
              <input
                id="live-bid-quantity"
                type="tel"
                inputmode="numeric"
                autocomplete="off"
                v-model="bidQuantity"
                class="h-full w-full min-w-0 bg-transparent px-3 text-lg tabular-nums placeholder:text-boba-grey placeholder:font-light outline-none"
                placeholder="1"
              />
            </div>
          </div>

          <button
            data-test="buy-now"
            type="button"
            class="h-12 w-full rounded-lg bg-boba-brown px-6 text-base md:text-lg font-semibold text-white hover:bg-boba-brown/90 active:bg-boba-brown focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-boba-brown focus-visible:ring-offset-2 focus-visible:ring-offset-boba-sand-light disabled:bg-boba-grey/60 disabled:text-boba-brown/60 disabled:shadow-none disabled:cursor-not-allowed transition-colors duration-150"
            @click="emit('bid', listing.current_price.price, Number(bidQuantity))"
            :disabled="!canBid(listing)"
          >
            Buy Now
          </button>
        </div>
        <fade-transition>
          <p
            v-if="
              !canBid(listing) &&
              milesRequired(listing) > authStore.balance.available
            "
            class="mt-2 flex items-start gap-1.5 text-sm text-red-700"
          >
            <i class="fa-solid fa-circle-exclamation mt-0.5" aria-hidden="true" />
            <span>Not enough miles available</span>
          </p>
        </fade-transition>
      </template>
    </login>
    <p
      v-else-if="DateTime.fromISO(listing.starts_at) < DateTime.now()"
      class="mt-4 inline-flex items-center gap-2 text-sm text-boba-grey-dark"
    >
      <i class="fa-regular fa-lock" aria-hidden="true" />
      This item is no longer available.
    </p>
  </section>
</template>
<script lang="ts" setup>
import { DateTime } from "ts-luxon";
import { computed, ref } from "vue";
import { Listing } from "@/types";
import ScoreboardCountdown from "@/components/ScoreboardCountdown.vue";
import { useAuth } from "@/pkg/auth";

const props = defineProps<{
  listing: Listing;
  countdownTo: DateTime;
}>();

const emit = defineEmits<{
  (e: "bid", amount: number, quantity: number): void;
  (e: "reload"): void;
}>();

const authStore = useAuth();
const bidQuantity = ref<number | string | null>(null);

/** A public per-purchase limit worth rendering as a select; -1 and 0 are not. */
const hasPublicLimit = computed(() => (props.listing.buy_limit ?? 0) > 0);

/**
 * Smallest applicable per-purchase cap, or Infinity when nothing caps it.
 * The API sends user_buy_limit -1 for "unlimited".
 */
const quantityLimit = computed(() => {
  const user = props.listing.user_buy_limit;
  const caps = [props.listing.buy_limit, user === -1 ? null : user].filter(
    (n): n is number => n != null,
  );
  return caps.length ? Math.min(...caps) : Number.POSITIVE_INFINITY;
});

const canBid = function (listing: Listing) {
  if (!listing) {
    return false;
  }

  if (
    bidQuantity.value === 0 ||
    bidQuantity.value === null ||
    Number(bidQuantity.value) < 1
  ) {
    return false;
  }

  if (String(bidQuantity.value).match(/\D/)) {
    return false;
  }

  if (
    Number.isFinite(quantityLimit.value) &&
    Number(bidQuantity.value) > quantityLimit.value
  ) {
    return false;
  }

  if (authStore.balance.available < milesRequired(listing)) {
    return false;
  }

  if (listing.user_buy_limit == -1) {
    return true;
  }

  if (listing.user_buy_limit <= 0) {
    return false;
  }

  return true;
};

function milesRequired(listing: Listing) {
  return Number(bidQuantity.value) * (listing?.current_price?.price ?? 99);
}

function callIfNotEnded() {
  if (props.listing.auction_status != "ended") {
    emit("reload");
  }
}
</script>
